import React, { useState } from 'react';
import { Rnd } from 'react-rnd';
import { Trash2, Plus, Save, X } from 'lucide-react';
import { CANVAS, WIDGET_TYPES, newWidget, emptyTemplate, WidgetContent } from './TemplateRenderer';
import { api } from '../api';

const EDITOR_WIDTH = 900;
const SCALE = EDITOR_WIDTH / CANVAS.w;

const panel = { background: '#111827', borderRadius: 10, padding: 16, color: '#e5e7eb' };
const field = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #334155', background: '#0b1020', color: '#e5e7eb', marginBottom: 10 };
const lbl = { display: 'block', fontSize: 12, color: '#9aa7bd', marginBottom: 4 };

export default function TemplateEditor({ template, userId, onSaved, onCancel }) {
  const [name, setName] = useState(template?.name || 'Untitled template');
  const initial = template?.json || emptyTemplate();
  const [background, setBackground] = useState(initial.background || '#0b1020');
  const [widgets, setWidgets] = useState(initial.widgets || []);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selected = widgets.find((w) => w.id === selectedId) || null;

  const addWidget = (type) => {
    const w = newWidget(type);
    setWidgets((prev) => [...prev, w]);
    setSelectedId(w.id);
  };

  const updateWidget = (id, patch) =>
    setWidgets((prev) => prev.map((w) => (w.id === id ? { ...w, ...patch } : w)));

  const updateProps = (id, propPatch) =>
    setWidgets((prev) => prev.map((w) => (w.id === id ? { ...w, props: { ...w.props, ...propPatch } } : w)));

  const removeWidget = (id) => {
    setWidgets((prev) => prev.filter((w) => w.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const json = { version: 1, background, widgets };
      const result = template?.id
        ? await api.updateTemplate(template.id, { name, json })
        : await api.createTemplate({ name, json, userId });
      onSaved && onSaved(result.template);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <input value={name} onChange={(e) => setName(e.target.value)}
          style={{ ...field, marginBottom: 0, maxWidth: 320, fontSize: 16, fontWeight: 600 }} />
        <button className="button" onClick={save} disabled={saving}>
          <Save size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          {saving ? 'Saving…' : 'Save template'}
        </button>
        <button className="button" style={{ background: '#374151' }} onClick={onCancel}>
          <X size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Cancel
        </button>
        {error && <span style={{ color: '#f87171' }}>{error}</span>}
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {/* Palette */}
        <div style={{ ...panel, width: 150 }}>
          <div style={{ fontSize: 12, color: '#9aa7bd', marginBottom: 8 }}>ADD WIDGET</div>
          {WIDGET_TYPES.map((t) => (
            <button key={t.type} onClick={() => addWidget(t.type)}
              style={{ ...field, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Plus size={14} /> {t.label}
            </button>
          ))}
          <div style={{ marginTop: 12 }}>
            <label style={lbl}>Background</label>
            <input type="color" value={background} onChange={(e) => setBackground(e.target.value)}
              style={{ width: '100%', height: 36, border: 'none', background: 'none' }} />
          </div>
        </div>

        {/* Canvas */}
        <div style={{ width: EDITOR_WIDTH, height: CANVAS.h * SCALE, position: 'relative', flexShrink: 0,
          borderRadius: 8, overflow: 'hidden', boxShadow: '0 0 0 1px #334155' }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setSelectedId(null); }}>
          <div style={{ width: CANVAS.w, height: CANVAS.h, transform: `scale(${SCALE})`, transformOrigin: 'top left', background, position: 'absolute' }}>
            {widgets.map((w) => (
              <Rnd key={w.id} scale={SCALE} bounds="parent"
                size={{ width: w.w, height: w.h }} position={{ x: w.x, y: w.y }}
                onDragStart={() => setSelectedId(w.id)}
                onDragStop={(e, d) => updateWidget(w.id, { x: Math.round(d.x), y: Math.round(d.y) })}
                onResizeStop={(e, dir, ref, delta, pos) => updateWidget(w.id, {
                  w: Math.round(parseFloat(ref.style.width)),
                  h: Math.round(parseFloat(ref.style.height)),
                  x: Math.round(pos.x), y: Math.round(pos.y),
                })}
                style={{ outline: selectedId === w.id ? '3px solid #3b82f6' : '1px dashed rgba(255,255,255,0.25)' }}
                onMouseDown={() => setSelectedId(w.id)}>
                <div style={{ width: '100%', height: '100%' }}><WidgetContent widget={w} /></div>
              </Rnd>
            ))}
          </div>
        </div>

        {/* Properties */}
        <div style={{ ...panel, flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 12, color: '#9aa7bd', marginBottom: 8 }}>PROPERTIES</div>
          {!selected && <div style={{ color: '#6b7a92' }}>Select a widget to edit its properties.</div>}
          {selected && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{selected.type}</span>
                <button onClick={() => removeWidget(selected.id)} title="Delete"
                  style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer' }}>
                  <Trash2 size={18} />
                </button>
              </div>
              <WidgetProps widget={selected} updateProps={updateProps} />
              <PositionFields widget={selected} updateWidget={updateWidget} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WidgetProps({ widget, updateProps }) {
  const p = widget.props || {};
  const set = (patch) => updateProps(widget.id, patch);
  switch (widget.type) {
    case 'text':
      return (
        <>
          <label style={lbl}>Text</label>
          <textarea value={p.text || ''} onChange={(e) => set({ text: e.target.value })} style={{ ...field, height: 80 }} />
          <ColorRow label="Color" value={p.color} onChange={(v) => set({ color: v })} />
          <NumRow label="Font size" value={p.fontSize} onChange={(v) => set({ fontSize: v })} />
          <SelectRow label="Align" value={p.textAlign} options={['left', 'center', 'right']} onChange={(v) => set({ textAlign: v })} />
          <SelectRow label="Weight" value={p.fontWeight} options={['400', '600', '700']} onChange={(v) => set({ fontWeight: v })} />
        </>
      );
    case 'image':
      return (
        <>
          <label style={lbl}>Image URL</label>
          <input value={p.src || ''} onChange={(e) => set({ src: e.target.value })} style={field} placeholder="https://…" />
          <SelectRow label="Fit" value={p.fit} options={['cover', 'contain', 'fill']} onChange={(v) => set({ fit: v })} />
        </>
      );
    case 'clock':
      return (
        <>
          <SelectRow label="Format" value={p.format} options={['24h', '12h']} onChange={(v) => set({ format: v })} />
          <SelectRow label="Show date" value={p.showDate === false ? 'no' : 'yes'} options={['yes', 'no']} onChange={(v) => set({ showDate: v === 'yes' })} />
          <ColorRow label="Color" value={p.color} onChange={(v) => set({ color: v })} />
          <NumRow label="Font size" value={p.fontSize} onChange={(v) => set({ fontSize: v })} />
        </>
      );
    case 'list':
      return (
        <>
          <label style={lbl}>Title</label>
          <input value={p.title || ''} onChange={(e) => set({ title: e.target.value })} style={field} />
          <label style={lbl}>Items (one per line)</label>
          <textarea value={(p.items || []).map((i) => (typeof i === 'object' ? `${i.label} | ${i.value}` : i)).join('\n')}
            onChange={(e) => set({ items: e.target.value.split('\n').filter(Boolean).map((line) => {
              const parts = line.split('|');
              return parts.length > 1 ? { label: parts[0].trim(), value: parts.slice(1).join('|').trim() } : line;
            }) })}
            style={{ ...field, height: 120 }} placeholder={'Plain line\nLabel | Value'} />
          <ColorRow label="Color" value={p.color} onChange={(v) => set({ color: v })} />
          <NumRow label="Font size" value={p.fontSize} onChange={(v) => set({ fontSize: v })} />
        </>
      );
    case 'shape':
      return (
        <>
          <ColorRow label="Fill" value={p.background} onChange={(v) => set({ background: v })} />
          <NumRow label="Corner radius" value={p.borderRadius} onChange={(v) => set({ borderRadius: v })} />
        </>
      );
    case 'html':
      return (
        <>
          <label style={lbl}>HTML</label>
          <textarea value={p.html || ''} onChange={(e) => set({ html: e.target.value })} style={{ ...field, height: 160, fontFamily: 'monospace' }} />
        </>
      );
    default:
      return null;
  }
}

function PositionFields({ widget, updateWidget }) {
  const num = (k) => (e) => updateWidget(widget.id, { [k]: parseInt(e.target.value || '0', 10) });
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
      {['x', 'y', 'w', 'h'].map((k) => (
        <div key={k} style={{ flex: 1 }}>
          <label style={lbl}>{k.toUpperCase()}</label>
          <input type="number" value={widget[k]} onChange={num(k)} style={field} />
        </div>
      ))}
    </div>
  );
}

const ColorRow = ({ label, value, onChange }) => (
  <div style={{ marginBottom: 10 }}>
    <label style={lbl}>{label}</label>
    <input type="color" value={value || '#ffffff'} onChange={(e) => onChange(e.target.value)} style={{ width: '100%', height: 34, border: 'none', background: 'none' }} />
  </div>
);
const NumRow = ({ label, value, onChange }) => (
  <div><label style={lbl}>{label}</label>
    <input type="number" value={value ?? ''} onChange={(e) => onChange(parseInt(e.target.value || '0', 10))} style={field} /></div>
);
const SelectRow = ({ label, value, options, onChange }) => (
  <div><label style={lbl}>{label}</label>
    <select value={value || options[0]} onChange={(e) => onChange(e.target.value)} style={field}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select></div>
);
